import { useEffect, useRef } from 'react';
import { FormArray } from '@core/utils/form';
import { FCRepeaterFormData } from '@modules/settings/SettingsPage/Components/FormsTab/FormFields/FCRepeater';
import { convertDecimalToFraction, isMeasurementField } from './measurementConversion';
import { Subscription } from 'rxjs';

/**
 * React hook that automatically converts decimal measurements to fractions
 * for laser measuring device input.
 *
 * This hook:
 * 1. Identifies "W (Inches)" and "H (Inches)" fields
 * 2. Subscribes to value changes on these fields
 * 3. Automatically converts decimal values to reduced fractions
 * 4. Updates corresponding fraction and increment fields
 *
 * @param form - The FormArray containing repeater items
 * @param field - The repeater field configuration
 * @param enabled - Whether laser conversion is enabled
 */
export function useLaserMeasurementConversion(form: FormArray<any>, field: FCRepeaterFormData, enabled: boolean) {
  const subscriptionsRef = useRef<Subscription[]>([]);

  useEffect(() => {
    // Check enabled first - if disabled, just clean up and return
    if (!enabled) {
      subscriptionsRef.current.forEach(sub => sub.unsubscribe());
      subscriptionsRef.current = [];
      return;
    }

    // Clear previous subscriptions only when enabled
    subscriptionsRef.current.forEach(sub => sub.unsubscribe());
    subscriptionsRef.current = [];

    // Find measurement field indices
    const measurementFields = field.items
      .map((item, index) => ({
        index,
        title: item.title,
        isMeasurement: isMeasurementField(item.title || ''),
        fieldType: getMeasurementFieldType(item.title || ''),
      }))
      .filter(item => item.isMeasurement);

    if (measurementFields.length === 0) return;

    // Subscribe to form items changes
    const formItemsSub = form.items$.subscribe(formItems => {
      // For each row in the repeater
      formItems.forEach((rowForm, rowIndex) => {
        // For each measurement field (W/H Inches)
        measurementFields.forEach(({ index: fieldIndex, fieldType }) => {
          const control = rowForm.controls[fieldIndex];

          if (!control) return;

          // Subscribe to value changes on this control
          const valueSub = control.value$.subscribe((value: any) => {
            // Only process if value is a valid decimal number
            const numValue = parseFloat(value);
            if (isNaN(numValue) || value === null || value === undefined || value === '') {
              return;
            }

            // Convert to fraction
            const result = convertDecimalToFraction(numValue);

            // Find corresponding fraction and increment fields
            const fractionFieldIndex = findFieldByTitle(field.items, `${fieldType} (Fractions)`);
            const incrementFieldIndex = findFieldByTitle(field.items, `${fieldType} (Increments)`);

            // Update fraction field if it exists
            if (fractionFieldIndex !== -1) {
              const fractionControl = rowForm.controls[fractionFieldIndex];
              // Map fraction value for dropdown: remove "+" suffix and handle "Even"
              const fractionDropdownValue = mapFractionToDropdown(result.fraction);
              if (fractionControl && fractionControl.value !== fractionDropdownValue) {
                fractionControl.setValue(fractionDropdownValue, { emitEvent: false });
              }
            }

            // Update increment field if it exists
            if (incrementFieldIndex !== -1) {
              const incrementControl = rowForm.controls[incrementFieldIndex];
              // Map increment value for dropdown: "Even", "Heavy (+)", or "Light (-)"
              const incrementDropdownValue = mapIncrementToDropdown(result.fraction);
              if (incrementControl && incrementControl.value !== incrementDropdownValue) {
                incrementControl.setValue(incrementDropdownValue, { emitEvent: false });
              }
            }
          });

          subscriptionsRef.current.push(valueSub);
        });
      });
    });

    subscriptionsRef.current.push(formItemsSub);

    // Cleanup subscriptions on unmount or when dependencies change
    return () => {
      subscriptionsRef.current.forEach(sub => sub.unsubscribe());
      subscriptionsRef.current = [];
    };
  }, [form, field, enabled]);
}

/**
 * Extracts the measurement type (W or H) from a field title
 */
function getMeasurementFieldType(title: string): string {
  const normalized = title.toLowerCase().trim();
  if (normalized.includes('w (inches)') || normalized === 'w (inches)') {
    return 'W';
  }
  if (normalized.includes('h (inches)') || normalized === 'h (inches)') {
    return 'H';
  }
  return '';
}

/**
 * Finds a field index by its title (case-insensitive)
 */
function findFieldByTitle(items: any[], title: string): number {
  const normalizedTitle = title.toLowerCase().trim();
  return items.findIndex(item => (item.title || '').toLowerCase().trim() === normalizedTitle);
}

/**
 * Maps the calculated fraction value to a dropdown option for Fractions field
 * Removes the "+" suffix since dropdown has exact fractions
 *
 * @param fraction - The fraction string from conversion (e.g., "1/4+", "1/2", "+")
 * @returns The dropdown value (e.g., "1/4", "1/2", "Even")
 */
function mapFractionToDropdown(fraction: string): string {
  if (!fraction || fraction === '+') {
    // No fraction or just "+" means it's essentially even (whole number with tiny overage)
    return 'Even';
  }

  // Remove the "+" suffix for dropdown selection
  // e.g., "1/4+" becomes "1/4"
  return fraction.replace('+', '');
}

/**
 * Maps the calculated fraction to the Increments dropdown value
 *
 * @param fraction - The fraction string from conversion (e.g., "1/4+", "1/2", "+")
 * @returns "Even" if no "+", "Heavy (+)" if has "+"
 */
function mapIncrementToDropdown(fraction: string): string {
  if (!fraction) {
    // No fraction means exact whole number
    return 'Even';
  }

  if (fraction.includes('+')) {
    // Any "+" means we rounded down, so it's "Heavy (+)"
    return 'Heavy (+)';
  }

  // Exact fraction match (no rounding) means "Even"
  return 'Even';
}
